import React, { useEffect, useRef, useState } from "react";
import { Stack } from "@fluentui/react";
import { ContentRegionLoader } from "../components/loading";
import { ChatCanvasEmptyState } from "../components/ChatCanvasEmptyState/ChatCanvasEmptyState";
import { ChatMessageContainer, type ConsoleChatTurn } from "../components/ChatMessageContainer/ChatMessageContainer";
import {
  CitationPanel,
  activeCitationFromProduct,
  type ActiveCitationTuple,
} from "../components/CitationPanel/CitationPanel";
import { QuestionInput } from "../components/QuestionInput";
import { isAskAnswer } from "../api/conversationIdentity";
import type {
  ConsoleAskResponse,
  ConsoleCitation,
  ConsoleRefusal,
  ConsoleTrailDetail,
} from "../api/types";
import chatStyles from "../pages/chat/Chat.module.css";

const CITATION_PANEL_DEFAULT_WIDTH = 560;

export type KnowledgeThreadProps = {
  clearanceNotice: string | null;
  asking: boolean;
  answer: ConsoleAskResponse | ConsoleRefusal | null;
  openTrail: ConsoleTrailDetail | null;
  onSubmit: (question: string) => void;
};

function turnsFromTrail(trail: ConsoleTrailDetail): ConsoleChatTurn[] {
  const turns: ConsoleChatTurn[] = [];
  for (const turn of trail.turns || []) {
    if (turn.prompt) {
      turns.push({ role: "user", content: turn.prompt });
    }
    if (turn.answer) {
      turns.push({
        role: "assistant",
        content: turn.answer,
        sources: turn.sources,
        citations: turn.citations,
      });
    }
  }
  return turns;
}

export function KnowledgeThread({
  clearanceNotice,
  asking,
  answer,
  openTrail,
  onSubmit,
}: KnowledgeThreadProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [pendingPrompt, setPendingPrompt] = useState<string | null>(null);
  const [activeCitation, setActiveCitation] = useState<ActiveCitationTuple | null>(
    null,
  );
  const [isCitationPanelOpen, setIsCitationPanelOpen] = useState(false);

  useEffect(() => {
    if (asking) {
      return;
    }
    const trailTurns = openTrail ? turnsFromTrail(openTrail) : [];
    const lastTrailUser = [...trailTurns]
      .reverse()
      .find((turn) => turn.role === "user");
    if (pendingPrompt && lastTrailUser?.content === pendingPrompt) {
      setPendingPrompt(null);
    }
  }, [asking, openTrail, pendingPrompt]);

  const trailTurns = openTrail ? turnsFromTrail(openTrail) : [];
  const lastTrailUser = [...trailTurns]
    .reverse()
    .find((turn) => turn.role === "user");
  const pendingUser: ConsoleChatTurn[] =
    pendingPrompt && lastTrailUser?.content !== pendingPrompt
      ? [{ role: "user", content: pendingPrompt }]
      : [];
  const hasSuccessAnswer = Boolean(answer && isAskAnswer(answer) && answer.answer);
  const streamingAssistant: ConsoleChatTurn[] =
    hasSuccessAnswer && answer && isAskAnswer(answer) && answer.answer
      ? [
          {
            role: "assistant",
            content: answer.answer,
            sources: answer.sources,
            citations: answer.citations,
          },
        ]
      : [];
  const thread = [...trailTurns, ...pendingUser, ...streamingAssistant];
  const empty = thread.length === 0 && !asking && !pendingPrompt && !answer;
  const showWaitDots = asking && !hasSuccessAnswer;
  const showCitationPanel = Boolean(isCitationPanelOpen && activeCitation);

  const onCitationClicked = (citation: ConsoleCitation) => {
    setActiveCitation(activeCitationFromProduct(citation));
    setIsCitationPanelOpen(true);
  };

  useEffect(() => {
    const node = scrollRef.current;
    if (node) {
      node.scrollTop = node.scrollHeight;
    }
  }, [thread.length, asking]);

  return (
    <div
      className={`${chatStyles.container} ${chatStyles.chatPageDesktopSplit}`}
      data-testid="console-work-blade"
      style={
        showCitationPanel
          ? ({
              ["--cordexa-right-panel-width" as string]: `${CITATION_PANEL_DEFAULT_WIDTH}px`,
            } as React.CSSProperties)
          : undefined
      }
    >
      <div
        className={`${chatStyles.chatContainer} ${
          showCitationPanel ? chatStyles.chatContainerWithRightPanel : ""
        }`}
      >
        <div ref={scrollRef} className={chatStyles.chatScrollWrapper}>
          <div className={chatStyles.chatMessageStream}>
            {empty ? <ChatCanvasEmptyState clearanceNotice={clearanceNotice} /> : null}
            {!empty && openTrail ? (
              <div data-testid="console-open-trail">
                <ChatMessageContainer
                  answers={thread}
                  onCitationClicked={onCitationClicked}
                />
              </div>
            ) : null}
            {!empty && !openTrail ? (
              <div data-testid="console-answer">
                <p data-testid="console-answer-outcome" className="sr-only">
                  {answer?.outcome_type}
                </p>
                {thread.length > 0 ? (
                  <ChatMessageContainer
                    answers={thread}
                    onCitationClicked={onCitationClicked}
                  />
                ) : null}
                {answer?.message && !hasSuccessAnswer ? (
                  <p data-testid="console-answer-message">{answer.message}</p>
                ) : null}
              </div>
            ) : null}
            {showWaitDots ? (
              <ContentRegionLoader label="Asking Cortex" data-testid="console-ask-loading" />
            ) : null}
          </div>
        </div>
        <div className={chatStyles.chatInputContainer} data-testid="chat-composer">
          <Stack horizontal className={chatStyles.chatInput}>
            <QuestionInput
              clearOnSend
              placeholder="Ask the knowledge base..."
              disabled={asking}
              isGenerating={asking}
              onSend={(question) => {
                setPendingPrompt(question);
                onSubmit(question);
              }}
            />
          </Stack>
        </div>
        <div className={chatStyles.chatDisclaimer}>AI-generated content may be incorrect</div>
      </div>
      <div
        className={`${chatStyles.citationPanelWrapper} ${
          showCitationPanel ? "" : chatStyles.citationPanelClosed
        }`}
        style={{ width: showCitationPanel ? CITATION_PANEL_DEFAULT_WIDTH : 0 }}
        data-testid="citation-panel-wrapper"
      >
        <div className={chatStyles.citationPanelContent}>
          {showCitationPanel ? (
            <CitationPanel
              activeCitation={activeCitation}
              setIsCitationPanelOpen={setIsCitationPanelOpen}
              isOpen={showCitationPanel}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

export { KnowledgeThread as WorkBlade };
